/**
 * 入口文件
 * 格式化请求参数，控制上报无线、wmda逻辑
 */

import { InitOptions, ClickExtParams } from './types/type';
import { isObject, parseParams, getTrackMode, isWx, isWmda } from './helpers';
import { getContextType, setContextType } from './vueInstance/contextManager';
import WxLog from './wxLog';
import WmdaLog from './wmdaLog';

// 使用私有变量存储实例状态
let _instance: {
    log: WxLog | null;
    wmdaLog: WmdaLog | null;
    trackMode: number | null;
    initialized: boolean;
} = {
    log: null,
    wmdaLog: null,
    trackMode: null,
    initialized: false
};

// 在vue上判断安装
function install(context: any, pageType: string | object, ...options: InitOptions[]) {
    try {
        if (_instance.initialized) {
            console.warn('【zp-weblog】埋点SDK已经初始化，避免重复初始化');
            return;
        }

        // 判断是 Vue 2 还是 Vue 3
        const isVue3 = context && context.config && context.config.hasOwnProperty('globalProperties');

        // 设置埋点方法
        if (isVue3) {
            context.config.globalProperties.$log = $log;
            context.provide('$log', $log);
        } else {
            context.prototype.$log = $log;
        }

        // 设置生命周期钩子
        const hookName = isVue3 ? 'beforeCreate' : 'created';
        context.mixin({
            [hookName]() {
                const contextType = getContextType();
                if (contextType?.name) return;

                setContextType({
                    name: 'vue',
                    route: this.$route
                });
            }
        });

        init(pageType, ...options);
    } catch (error) {
        console.error(`【zp-weblog】${error instanceof Error ? error.message : String(error)}`);
    }
}

// 埋点初始化
export function init(pageType: string | object, ...options: InitOptions[]) {
    try {
        // 格式化初始化参数，转换为标准的json结构
        let __options;
        if (isObject(pageType)) {
            // 当pageType是对象时，将其作为数组的第一个元素传入
            __options = parseParams([{ ...(pageType as object) }, ...options]);
        } else {
            // 当pageType是字符串时，创建一个带pageType键的对象作为数组的第一个元素
            __options = parseParams([{ pageType: pageType as string }, ...options]);
        }

        // 如果已经初始化，则更新初始化参数
        if (_instance.initialized) {
            // 更新初始化参数
            _instance.log?.updateInitParams(__options);
            _instance.wmdaLog?.updateInitParams(__options);
            // console.warn('【zp-weblog】埋点SDK已经初始化，避免重复初始化')
            return;
        }

        _instance.trackMode = getTrackMode(__options);

        if (isWx(_instance.trackMode)) {
            _instance.log = new WxLog(__options);
        }

        if (isWmda(_instance.trackMode)) {
            _instance.wmdaLog = new WmdaLog(__options);
        }

        _instance.initialized = true;

        // 仅在非模块环境下挂载到全局
        if (typeof window !== 'undefined') {
            window.$log = $log;
        }
    } catch (error) {
        console.error(`【zp-weblog】${error instanceof Error ? error.message : String(error)}`);
    }
}

// 无线埋点上报
function $wxLog(actionType: string, params: ClickExtParams) {
    try {
        if (!_instance.log) {
            console.warn('无线埋点未初始化，无法上报！');
            return;
        }

        _instance.log.clickLog(actionType, params);
    } catch (error) {
        console.error(`【zp-weblog】$log无线埋点上报异常: ${error}`);
    }
}

// wmda埋点上报
function $wmdaLog(eventId: string, params: ClickExtParams) {
    try {
        if (!_instance.wmdaLog) {
            console.warn('【zp-weblog】wmda埋点未初始化，无法上报！');
            return;
        }
        _instance.wmdaLog.clickLog(eventId, params);
    } catch (error) {
        console.error(`【zp-weblog】$log wmda埋点上报异常: ${error}`);
    }
}

// 埋点上报对外暴露函数
export function $log(actionType: string, ...params: ClickExtParams[]) {
    if (isWx(_instance.trackMode)) {
        $wxLog(actionType, parseParams(params));
    }

    if (isWmda(_instance.trackMode)) {
        $wmdaLog(actionType, parseParams(params));
    }
}

// 重置方法 - 用于测试或特殊场景
export function reset() {
    _instance = {
        log: null,
        wmdaLog: null,
        trackMode: null,
        initialized: false
    };
}

const tracking = {
    install,
    init,
    $log,
    reset,
    // 提供版本号便于调试
    version: '1.0.0'
};

// 仅在非模块环境下挂载到全局
if (typeof window !== 'undefined') {
    window.tracking = tracking;
}

export default tracking;
